import React, { useState, useEffect } from 'react';
import { Settings, LogOut, ChevronRight, User, Heart, Bookmark, Bell, Shield, HelpCircle, Loader2, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';

const API = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, isVenue } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [stats, setStats] = useState({ saved: 0, lists: 0, been_there: 0 });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const fetchStats = async () => {
      try {
        const [savedRes, listsRes, btRes] = await Promise.all([
          axios.get(`${API}/api/saves`, { withCredentials: true }),
          axios.get(`${API}/api/lists`, { withCredentials: true }),
          axios.get(`${API}/api/users/been-there`, { withCredentials: true })
        ]);
        setStats({
          saved: savedRes.data.activities?.length || 0,
          lists: listsRes.data?.length || 0,
          been_there: btRes.data.total || 0
        });
      } catch (err) { /* ignore */ }
    };
    fetchStats();
  }, [user, navigate]);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate('/', { replace: true });
  };

  if (!user) return null;

  const menuItems = [
    { icon: User, label: 'Edit Profile', path: '/profile/edit' },
    { icon: Heart, label: 'Saved Activities', path: '/saved' },
    { icon: Camera, label: 'Adventure Journal', path: '/journal' },
    { icon: Bookmark, label: 'My Lists', path: '/saved?tab=lists' },
    { icon: Bell, label: 'Notifications', path: '/notifications' },
    { icon: Settings, label: 'Preferences', path: '/onboarding' },
    { icon: HelpCircle, label: 'Help & Support', path: '/help' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      {/* Header */}
      <div className="bg-white">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Profile Card */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-full bg-[#81B29A]/20 flex items-center justify-center overflow-hidden">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl text-[#81B29A] font-semibold">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#2C302E] font-['Outfit']">{user.name}</h1>
              <p className="text-[#666B67]">{user.email}</p>
              <span className="badge badge-secondary mt-2 capitalize">{user.role}</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center cursor-pointer" onClick={() => navigate('/saved')} data-testid="profile-stat-saved">
              <p className="text-2xl font-bold text-[#E07A5F]">{stats.saved}</p>
              <p className="text-xs text-[#666B67]">Saved</p>
            </div>
            <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center cursor-pointer" onClick={() => navigate('/journal')} data-testid="profile-stat-journal">
              <p className="text-2xl font-bold text-[#81B29A]">{stats.been_there}</p>
              <p className="text-xs text-[#666B67]">Been There</p>
            </div>
            <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center cursor-pointer" onClick={() => navigate('/saved?tab=lists')} data-testid="profile-stat-lists">
              <p className="text-2xl font-bold text-amber-500">{stats.lists}</p>
              <p className="text-xs text-[#666B67]">Lists</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Admin/Venue Dashboard Links */}
        {(isAdmin || isVenue) && (
          <div className="mb-6">
            <h2 className="text-sm font-medium text-[#666B67] uppercase tracking-wider mb-3">Dashboard</h2>
            <div className="bg-white rounded-2xl overflow-hidden">
              {isVenue && (
                <button
                  onClick={() => navigate('/venue/dashboard')}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#F9F8F6] transition-colors border-b border-[#2C302E]/5"
                  data-testid="venue-dashboard-link"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E07A5F]/10 flex items-center justify-center">
                      <Settings className="w-5 h-5 text-[#E07A5F]" />
                    </div>
                    <span className="font-medium text-[#2C302E]">Venue Dashboard</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#666B67]" />
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#F9F8F6] transition-colors"
                  data-testid="admin-dashboard-link"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#81B29A]/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-[#81B29A]" />
                    </div>
                    <span className="font-medium text-[#2C302E]">Admin Dashboard</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#666B67]" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Menu Items */}
        <div className="mb-6">
          <h2 className="text-sm font-medium text-[#666B67] uppercase tracking-wider mb-3">Account</h2>
          <div className="bg-white rounded-2xl overflow-hidden">
            {menuItems.map((item, index) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center justify-between p-4 hover:bg-[#F9F8F6] transition-colors ${
                  index < menuItems.length - 1 ? 'border-b border-[#2C302E]/5' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-[#666B67]" />
                  <span className="text-[#2C302E]">{item.label}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#666B67]" />
              </button>
            ))}
          </div>
        </div>

        {/* Logout */}
        <Button
          variant="outline"
          onClick={handleLogout}
          disabled={loggingOut}
          className="w-full py-6 rounded-2xl border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300"
          data-testid="logout-btn"
        >
          {loggingOut ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <LogOut className="w-5 h-5 mr-2" />
              Sign Out
            </>
          )}
        </Button>
      </main>

      <BottomNav />
    </div>
  );
};

export default Profile;
